import { useCallback, useEffect, useRef } from "react";
import { useMultiSiteContext } from "@hooks/use-site-context";
import { useScreenAlert } from "@hooks/use-screen-alert";
import { useUserContext } from "@hooks/use-auth-context";
import { useRefreshTokenMutation } from "@hooks/use-refresh-token-mutation";
import { fetchWithTokenRefresh } from "@hooks/shared/fetch-with-token-refresh";
import type { DynamicFilterState } from "@hooks/use-report-filter";
import { isValidColumnName, isValidFilterLength } from "@hooks/use-get/utils";

export type AggregateAssetsParams = {
    groupBy: readonly string[];
    sum?: readonly string[];
    // Dynamic filters - accepts any field from DynamicFilterState
    dynamicFilters?: DynamicFilterState;
    // Post type - 'asset', 'ticket', or 'user'
    postType?: 'asset' | 'ticket' | 'user';
    // Backend date-bucketing granularity for date fields in groupBy. Omit for the
    // server's default ('month') — only pass 'year' when every date-based chart
    // consuming this fetch wants year-level buckets.
    dateGranularity?: 'month' | 'year';
};

export type AggregateAssetResult = {
    [key: string]: string | number;
};

type CacheEntry = {
    data: AggregateAssetResult[];
    total: number;
    promise: Promise<{ data: AggregateAssetResult[]; total: number }>;
    isComplete: boolean;
};

// Share aggregate responses across all hook consumers so background prefetches
// are reused when later UI surfaces request the same grouped data. Keyed by
// `${email}:${url}` (not just the URL) so a different user logging into the
// same tab/site never reuses a prior user's cached counts.
const aggregateRequestCache = new Map<string, CacheEntry>();

/** Call on logout so the next user (same tab/site) never reuses a stale entry. */
export const clearAggregateAssetsCache = () => {
    aggregateRequestCache.clear();
};

export const useGetAggregateAssets = () => {
    const { setError } = useScreenAlert();
    const { user } = useUserContext();
    const { refreshUserToken } = useRefreshTokenMutation();

    const { getApiUrl } = useMultiSiteContext();

    // Read via ref (not the `user` state) inside the callback below so its
    // identity stays stable across token refreshes — otherwise every consumer
    // built on top of it (useReportData's fetch effect, refetch() effects in
    // GeneralReport, etc.) treats a background token refresh as "the report
    // needs to reload".
    const userRef = useRef(user);
    useEffect(() => {
        userRef.current = user;
    }, [user]);

    const getAggregateAssets = useCallback(
        async ({ groupBy, sum, dynamicFilters, postType = 'asset', dateGranularity }: AggregateAssetsParams) => {
            // Nothing to group by — return empty result immediately rather than sending
            // a malformed request that the server will reject with 400.
            if (!groupBy || groupBy.length === 0) {
                return { data: [], total: 0 };
            }
            try {
                // Determine endpoint based on post type
                const endpoint = postType === 'ticket'
                    ? 'wp-json/smapp/v1/aggregate/tickets'
                    : postType === 'user'
                    ? 'wp-json/smapp/v1/aggregate/users'
                    : 'wp-json/smapp/v1/aggregate/assets';
                const aggregateUrl = getApiUrl(endpoint);
                
                // Build URL with groupby[] array params
                const url = new URL(aggregateUrl);
                
                groupBy.forEach((field) => {
                    if (!isValidColumnName(field)) return;
                    url.searchParams.append("groupby[]", field);
                });
                
                // Add dynamic filters from filter state
                if (dynamicFilters) {
                    Object.entries(dynamicFilters).forEach(([columnName, value]) => {
                        // Reject filters with invalid column names to prevent injection
                        if (!isValidColumnName(columnName)) return;

                        if (Array.isArray(value)) {
                            // Array-based field (select, taxonomy, boolean, etc.)
                            if (value.length > 0) {
                                value.forEach((item) => {
                                    if (!isValidFilterLength(item)) return;
                                    url.searchParams.append(`filters[${columnName}][]`, item);
                                });
                            }
                        } else if (typeof value === "object" && value !== null && "start" in value && "end" in value) {
                            // Date range field
                            if (value.start && isValidFilterLength(value.start)) {
                                url.searchParams.set(`filters[${columnName}][start]`, value.start);
                            }
                            if (value.end && isValidFilterLength(value.end)) {
                                url.searchParams.set(`filters[${columnName}][end]`, value.end);
                            }
                        }
                    });
                }
                
                if (sum?.length) {
                    sum.forEach((field) => {
                        if (!isValidColumnName(field)) return;
                        url.searchParams.append("sum[]", field);
                    });
                }

                if (dateGranularity) {
                    url.searchParams.set("date_granularity", dateGranularity);
                }

                const urlString = url.toString();
                const cache = aggregateRequestCache;
                // Scope by user identity — the URL alone doesn't change when a
                // different user logs into the same site in the same tab.
                const cacheKey = `${userRef.current?.email ?? ""}:${urlString}`;

                // Check if we have a cached result or in-flight request
                if (cache.has(cacheKey)) {
                    const cached = cache.get(cacheKey)!;
                    
                    // If request is complete, return cached result
                    if (cached.isComplete) {
                        return { data: cached.data, total: cached.total };
                    }
                    
                    // Otherwise, wait for the in-flight request to complete
                    const result = await cached.promise;
                    return result;
                }

                // Create the fetch promise
                const fetchPromise = (async () => {
                    const response = await fetchWithTokenRefresh(
                        urlString,
                        {
                            method: "GET",
                            headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${userRef.current?.authToken ?? ""}`,
                            },
                        },
                        () => refreshUserToken(userRef.current?.refreshToken ?? "")
                    );
                    
                    if (!response.ok) {
                        // 400 = unsupported groupBy; 404 = endpoint disabled (e.g. user extension off)
                        if (response.status === 400 || response.status === 404) {
                            return { data: [], total: 0 };
                        }
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }
                    
                    const json = await response.json();
                    // API returns { groupBy, total, data: [...] } wrapper
                    const items = Array.isArray(json) ? json : (json?.data ?? []);
                    const total = Array.isArray(json) ? json.length : (json?.total ?? items.length);
                    
                    const result = { data: items, total };
                    return result;
                })();

                // Store the promise in cache to mark request as in-flight
                cache.set(cacheKey, { data: [], total: 0, promise: fetchPromise, isComplete: false });

                // Wait for result and update cache with the actual data
                const result = await fetchPromise;
                cache.set(cacheKey, { data: result.data, total: result.total, promise: fetchPromise, isComplete: true });
                return result;
            } catch (err) {
                setError("Error fetching aggregate assets: " + (err as Error)?.message);
                return { data: [], total: 0 };
            }
        },
        [getApiUrl, setError, refreshUserToken]
    );

    return { getAggregateAssets };
};
